import { FC, useState } from "react";
import { Bell, Users } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import ManageGroupSubscriptionModal from "@/components/modals/ManageGroupSubscriptionModal";
import { Permission } from "@/lib/permissions/Permission.enum";
import { permissionMap } from "@/lib/permissions/permissionMap";
import useUserTokenStore from "@/stores/user-token.store";
import { cn } from "@/lib/utils";

export type LegislationMetadataPreview = {
  datasource: string;
  legislationExternalId?: string | null;
  publishedOn?: string | null;
  isCurrent?: boolean | null;
  documentTypeId?: string | number | null;
};

type SubscribeAsGroupButtonProps = {
  subscribeCategory: SubscriptionCategory;
  entityPath: string;
  entityName: string;
  source: string;
  legislationExternalId?: string;
  legislationDatasource?: string;
  /** When true, a group (the user is in) is subscribed to this entity; show bell as filled/amber. */
  isGroupSubscribed?: boolean;
  legislationMetadata?: LegislationMetadataPreview;
  className?: string;
  strokeWidth?: number;
  container?: HTMLElement;
  onOperationStart?: () => void;
  onOperationEnd?: () => void;
  /** Controlled mode: parent controls modal. When provided with onOpenChange, call this when opening so parent can set which entity. */
  onOpenRequest?: () => void;
  /** Controlled mode: open state from parent. */
  open?: boolean;
  /** Controlled mode: parent handles open change (e.g. close). */
  onOpenChange?: (open: boolean) => void;
};

const SubscribeAsGroupButton: FC<SubscribeAsGroupButtonProps> = ({
  subscribeCategory,
  entityPath,
  entityName,
  source,
  legislationExternalId,
  legislationDatasource,
  isGroupSubscribed = false,
  legislationMetadata,
  className,
  strokeWidth = 2,
  container,
  onOperationStart,
  onOperationEnd,
  onOpenRequest,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const variant =
    subscribeCategory === SubscriptionCategory.Legislation
      ? ("legislation" as const)
      : ("default" as const);

  const permissions = useUserTokenStore((s) => s.permissions);
  const canManageGroupSubscriptions = permissions.includes(
    permissionMap[Permission.CreateFeatureGroups]
  );

  const [internalOpen, setInternalOpen] = useState(false);

  // Non-admins: only show the group bell when a group is subscribed (read-only). Never show greyed-out or on hover.
  if (!canManageGroupSubscriptions && !isGroupSubscribed) {
    return null;
  }
  const isControlled = controlledOnOpenChange !== undefined;
  const modalOpen = isControlled ? (controlledOpen ?? false) : internalOpen;
  const setModalOpen = isControlled
    ? (open: boolean) => controlledOnOpenChange?.(open)
    : setInternalOpen;

  const iconContent = (
    <span className="relative inline-flex items-center justify-center">
      <Bell
        className={cn(
          "size-4",
          isGroupSubscribed && "fill-warning stroke-warning"
        )}
        strokeWidth={strokeWidth}
      />
      <Users
        className={cn(
          "absolute -left-0.5 -top-0.5 size-2.5 rounded-full bg-background stroke-2",
          isGroupSubscribed ? "text-warning" : "text-muted-foreground"
        )}
        strokeWidth={2.5}
        aria-hidden
      />
    </span>
  );

  // Read-only for non-admins when group is subscribed: show lit bell only; swallow click so row doesn't receive it; slightly greyed to indicate non-interactive
  const readOnlyButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            className ?? "p-2",
            "pointer-events-auto opacity-70 cursor-default"
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          id="subscribe-as-group-button"
          aria-label="Gruppe abonneret"
        >
          {iconContent}
        </Button>
      </TooltipTrigger>
      <TooltipContent container={container}>
        Gruppe abonneret
      </TooltipContent>
    </Tooltip>
  );

  const handleOpenModal = () => {
    onOpenRequest?.();
    setModalOpen(true);
    onOperationStart?.();
  };

  const buttonWithTooltip = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className ?? "p-2"}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal();
          }}
          id="subscribe-as-group-button"
        >
          {iconContent}
        </Button>
      </TooltipTrigger>
      <TooltipContent container={container}>
        Abonner gruppe
      </TooltipContent>
    </Tooltip>
  );

  if (!canManageGroupSubscriptions) {
    return <>{readOnlyButton}</>;
  }

  return (
    <>
      {buttonWithTooltip}
      {!isControlled && (
        <ManageGroupSubscriptionModal
          open={modalOpen}
          onOpenChange={(open) => {
            setModalOpen(open);
            if (!open) onOperationEnd?.();
          }}
          variant={variant}
          entityPath={entityPath}
          entityName={entityName}
          source={source}
          subscribeCategory={subscribeCategory}
          legislationExternalId={legislationExternalId}
          legislationDatasource={legislationDatasource}
          legislationMetadata={legislationMetadata}
        />
      )}
    </>
  );
};

export default SubscribeAsGroupButton;